import type { CardDetail, CardSummary, FxRate, Holding, Portfolio } from './types.ts';

class HttpError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}
export { HttpError };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { ...init.headers, accept: 'application/json' },
  });
  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }
    throw new HttpError(res.status, `${res.status} ${res.statusText} — ${detail}`);
  }
  return (await res.json()) as T;
}

export interface MeResponse {
  authenticated: boolean;
  user?: { id: string; handle: string; role: 'operator' | 'friend' };
}

export function me(): Promise<MeResponse> {
  return request('/api/me');
}

export function login(handle: string, password: string): Promise<{ ok: true }> {
  return request('/api/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ handle, password }),
  });
}

export function signup(
  handle: string,
  password: string,
): Promise<{ ok: true; user: { id: string; handle: string; role: string } }> {
  return request('/api/auth/signup', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ handle, password }),
  });
}

export function logout(): Promise<{ ok: true }> {
  return request('/api/logout', { method: 'POST' });
}

export async function listCards(): Promise<CardSummary[]> {
  const r = await request<{ cards: CardSummary[] }>('/api/cards');
  return r.cards;
}

export async function cardDetail(id: string): Promise<CardDetail> {
  const r = await request<{ card: CardDetail }>(`/api/cards/${encodeURIComponent(id)}`);
  return r.card;
}

export function fx(): Promise<FxRate> {
  return request('/api/fx');
}

export async function listHoldings(
  status: 'open' | 'closed' = 'open',
  mode: 'real' | 'paper' = 'real',
): Promise<Holding[]> {
  const r = await request<{ holdings: Holding[] }>(`/api/holdings?status=${status}&mode=${mode}`);
  return r.holdings;
}

export function createHolding(input: {
  card_id: string;
  qty: number;
  buy_hkd: number;
  buy_at: string;
  mode?: 'real' | 'paper';
  notes?: string;
}): Promise<{ ok: true; id: string }> {
  return request('/api/holdings', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function sellHolding(
  id: string,
  input: { sell_hkd: number; sell_at: string },
): Promise<{ ok: true }> {
  return request(`/api/holdings/${encodeURIComponent(id)}/sell`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function deleteHolding(id: string): Promise<{ ok: true }> {
  return request(`/api/holdings/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function portfolio(mode: 'real' | 'paper' = 'real'): Promise<Portfolio> {
  return request(`/api/portfolio?mode=${mode}`);
}

export function queueCard(
  url: string,
): Promise<{
  ok: true;
  cardId: string;
  status: 'queued' | 'already-tracked';
  fetchedMetadata?: boolean;
}> {
  return request('/api/cards/queue', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url }),
  });
}
